import { useState, useEffect, useRef, useMemo } from 'react'

import {
  Tooltip,
  useGlobalState,
  useUpdateDocumentDetails,
  useMessage,
} from '@pepper/ui'

import {
  MAX_NAME_LENGTH,
  MIN_NAME_LENGTH,
  getDocumentPrefix,
  isDocTitleEditable,
} from './DocumentTitle.utils'
import styles from './_.module.css'

interface Props {
  docId: string
  docType: PepperDocTypes
  initialTitle?: string
  platform: Platforms
}

function DocumentTitle({ docId, docType, initialTitle = '', platform }: Props) {
  const { assignmentDetails } = useGlobalState()
  const { showError } = useMessage()

  const updateDocument = useUpdateDocumentDetails()

  const [title, setTitle] = useState(initialTitle)
  const [inputWidth, setInputWidth] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const calculateDummySpanWidth = (text: string) => {
      const span = document.createElement('span')
      span.textContent = text
      span.className = styles.titleInput
      document.body.appendChild(span)
      const newWidth = span.offsetWidth
      document.body.removeChild(span)
      return newWidth
    }

    if (inputRef.current) {
      const newInputWidth = calculateDummySpanWidth(title)
      setInputWidth(newInputWidth)
    }
  }, [title])

  useEffect(() => {
    setTitle(initialTitle)
  }, [docType, initialTitle])

  const itemCode = useMemo(() => {
    if (docType === 'PEPPER_DOC') {
      return assignmentDetails?.assignment_code
    }
    return null
  }, [assignmentDetails?.assignment_code, docType])

  const handleUpdateTitle = (name: string) => {
    if (name.length < MIN_NAME_LENGTH || name.length > MAX_NAME_LENGTH) {
      showError(
        `Please enter name within ${MIN_NAME_LENGTH} to ${MAX_NAME_LENGTH} character length`
      )
      inputRef.current?.focus()
      return
    }

    updateDocument.mutate(
      { docId, body: { title: name } },
      {
        onError: (error: unknown) => {
          if (error instanceof Error) showError(error.message)
          setTitle(initialTitle)
        },
      }
    )
  }

  return (
    <div className={styles.container}>
      <span className={styles.prefix}>
        {getDocumentPrefix(docType, '/')}&nbsp;
      </span>
      <Tooltip title={title}>
        <span>
          <input
            ref={inputRef}
            title={title}
            style={{ width: inputWidth }}
            disabled={!isDocTitleEditable({ docType, platform })}
            className={styles.titleInput}
            value={title}
            onChange={event => setTitle(event.target.value)}
            onBlur={event => handleUpdateTitle(event.target.value)}
          />
        </span>
      </Tooltip>
      {itemCode && <span className={styles.code}>#{itemCode}</span>}
    </div>
  )
}

export default DocumentTitle
